import { useState, useEffect, useMemo } from 'react';
import { ACCOUNT_TYPES, inferAccountType } from '../../constants/accountTypes';
import { ORG_TYPES, ORG_TYPE_LABELS, isIndividual, isNonProfit, getOrgInfo, saveOrgInfo } from '../../constants/orgTypes';
import { DEFAULT_ACCOUNTS } from '../../constants/defaultAccounts';
import { formatCurrency, parseAmount, formatAmountInput, generateId } from '../../utils/formatting';
import { getCurrentFiscalYear, clampDate } from '../../utils/date';
import { AccountSelect } from '../common';

// Helper function to format numbers
const formatNumber = (num) => {
    return new Intl.NumberFormat('ja-JP').format(num);
};

export const DataManagement = ({ accounts, journals, setAccounts, setJournals, showAlert, currentFiscalYear, fiscalYears, setFiscalYears, createNewFiscalYear, fileSystem, saveData, deletedJournals, setDeletedJournals, orgType, user, detailSettings, setDetailSettings, allocationSettings, setAllocationSettings, orgInfo, setOrgInfo, creditCards, setCreditCards, isBookOwner }) => {
    const showAllocation = isNonProfit(orgType);
    const isKojin = isIndividual(orgType);
    const [newYear, setNewYear] = useState(getCurrentFiscalYear() + 1);
    const [activeTab, setActiveTab] = useState('year');

    // メンバー管理
    const [members, setMembers] = useState([]);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [isInviting, setIsInviting] = useState(false);
    const [membersLoaded, setMembersLoaded] = useState(false);

    useEffect(() => {
        if (isBookOwner && fileSystem && !membersLoaded) {
            Promise.all([
                fileSystem.getMembers(),
                fileSystem.getInvitations()
            ]).then(([m, inv]) => {
                setMembers(m || []);
                setPendingInvites(inv || []);
                setMembersLoaded(true);
            });
        }
    }, [isBookOwner, fileSystem, membersLoaded]);

    const handleInviteMember = async () => {
        if (isInviting) return;
        const email = inviteEmail.trim().toLowerCase();
        if (!email || !email.includes('@')) {
            showAlert('有効なメールアドレスを入力してください', 'error');
            return;
        }
        setIsInviting(true);
        try {
            await fileSystem.inviteMember(email);
            // 保留中一覧を再取得して反映
            const invitationsResult = await fileSystem.getInvitations();
            setPendingInvites(invitationsResult || []);
            setInviteEmail('');
            showAlert(`${email} に招待メールを送信しました。`);
        } catch (err) {
            showAlert(err.message || '招待に失敗しました', 'error');
        } finally {
            setIsInviting(false);
        }
    };

    const handleCancelInvite = async (token) => {
        try {
            await fileSystem.cancelInvite(token);
            setPendingInvites(prev => prev.filter(inv => inv.token !== token));
            showAlert('招待を取り消しました');
        } catch (err) {
            showAlert(err.message || '取り消しに失敗しました', 'error');
        }
    };

    const INVITE_EXPIRY_MS = 24 * 60 * 60 * 1000;
    const isInviteExpired = (createdAt) => Date.now() - new Date(createdAt).getTime() > INVITE_EXPIRY_MS;

    const handleReinvite = async (inv) => {
        if (isInviting) return;
        setIsInviting(true);
        try {
            // 古いトークンを取り消してから再招待（inviteMember内で同メアドの既存トークンは自動削除される）
            await fileSystem.inviteMember(inv.inviteeEmail);
            const invitationsResult = await fileSystem.getInvitations();
            setPendingInvites(invitationsResult || []);
            showAlert(`${inv.inviteeEmail} に再招待メールを送信しました。`);
        } catch (err) {
            showAlert(err.message || '再招待に失敗しました', 'error');
        } finally {
            setIsInviting(false);
        }
    };

    const handleRemoveMember = async (email) => {
        if (!confirm(`${email} をメンバーから削除しますか？`)) return;
        try {
            await fileSystem.removeMember(email);
            setMembers(prev => prev.filter(m => m.email !== email));
            showAlert(`${email} を削除しました`);
        } catch (err) {
            showAlert(err.message || '削除に失敗しました', 'error');
        }
    };

    const [editOrgType, setEditOrgType] = useState(orgType);
    const [editOrgSubType, setEditOrgSubType] = useState(orgInfo?.subType || 'nonprofit');
    const [editOrgName, setEditOrgName] = useState(orgInfo?.name || '');
    const [editFoundingYear, setEditFoundingYear] = useState(orgInfo?.foundingYear || '');
    const [isSavingOrg, setIsSavingOrg] = useState(false);

    // タブ切り替えハンドラ（保存していない変更を破棄）
    const handleTabChange = (newTab) => {
        // 家事按分タブから離れる時、保存していない変更を元に戻す
        if (activeTab === 'apportionment' && newTab !== 'apportionment' && savedApportionmentSettings) {
            setApportionmentSettings(savedApportionmentSettings);
        }
        setActiveTab(newTab);
    };

    const [vendors, setVendors] = useState(orgInfo?.vendors || []);

    const deleteVendor = async (invoiceNumber) => {
        const newVendors = vendors.filter(v => v.invoiceNumber !== invoiceNumber);
        setVendors(newVendors);
        const updatedOrg = { ...orgInfo, vendors: newVendors };
        await saveOrgInfo(updatedOrg, activeBookEmail || user?.email);
        setOrgInfo(updatedOrg);
        showAlert('取引先を削除しました');
    };

    // クレジットカード編集
    const [editingCard, setEditingCard] = useState(null);
    const [cardForm, setCardForm] = useState({
        name: '', closingDay: 31, paymentDay: 27, paymentMonthOffset: 1,
        paymentAccountId: '', unpaidAccountId: '', openingBalance: 0, openingBalanceDate: ''
    });
    const [isSavingCards, setIsSavingCards] = useState(false);

    const assetAccounts = useMemo(() => accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.ASSET), [accounts]);
    const liabilityAccounts = useMemo(() => accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.LIABILITY), [accounts]);
    const defaultUnpaidAccount = useMemo(() => accounts.find(a => a.name === '未払金'), [accounts]);

    const resetCardForm = () => {
        setCardForm({
            name: '', closingDay: 31, paymentDay: 27, paymentMonthOffset: 1,
            paymentAccountId: '', unpaidAccountId: defaultUnpaidAccount?.id || '', openingBalance: 0, openingBalanceDate: ''
        });
        setEditingCard(null);
    };

    const handleSaveCard = async () => {
        if (!cardForm.name.trim()) {
            showAlert('カード名を入力してください', 'error');
            return;
        }
        setIsSavingCards(true);
        try {
            const card = {
                id: editingCard || generateId(),
                name: cardForm.name.trim(),
                closingDay: cardForm.closingDay,
                paymentDay: cardForm.paymentDay,
                paymentMonthOffset: cardForm.paymentMonthOffset,
                paymentAccountId: cardForm.paymentAccountId,
                unpaidAccountId: cardForm.unpaidAccountId || defaultUnpaidAccount?.id || '',
                openingBalance: Number(cardForm.openingBalance) || 0,
                openingBalanceDate: cardForm.openingBalanceDate || ''
            };

            let newCards;
            if (editingCard) {
                newCards = creditCards.map(c => c.id === editingCard ? card : c);
            } else {
                newCards = [...creditCards, card];
            }

            const success = await fileSystem.saveCreditCards(newCards);
            if (success) {
                setCreditCards(newCards);
                resetCardForm();
                showAlert(editingCard ? 'カードを更新しました' : 'カードを登録しました');
            } else {
                showAlert('保存に失敗しました', 'error');
            }
        } catch (err) {
            console.error('Failed to save credit card:', err);
            showAlert('保存に失敗しました', 'error');
        } finally {
            setIsSavingCards(false);
        }
    };

    const handleEditCard = (card) => {
        setEditingCard(card.id);
        setCardForm({
            name: card.name,
            closingDay: card.closingDay,
            paymentDay: card.paymentDay,
            paymentMonthOffset: card.paymentMonthOffset,
            paymentAccountId: card.paymentAccountId,
            unpaidAccountId: card.unpaidAccountId,
            openingBalance: card.openingBalance || 0,
            openingBalanceDate: card.openingBalanceDate || ''
        });
    };

    const handleDeleteCard = async (cardId) => {
        if (!confirm('このカードを削除してよろしいですか？')) return;
        const newCards = creditCards.filter(c => c.id !== cardId);
        const success = await fileSystem.saveCreditCards(newCards);
        if (success) {
            setCreditCards(newCards);
            showAlert('カードを削除しました');
        }
    };

    const [apportionmentSettings, setApportionmentSettings] = useState({
        version: 1,
        ratios: {},
        updatedAt: null
    });
    const [savedApportionmentSettings, setSavedApportionmentSettings] = useState(null); // 最後に保存した状態
    const [isLoadingApportionment, setIsLoadingApportionment] = useState(false);
    const [isSavingApportionment, setIsSavingApportionment] = useState(false);
    const [selectedApportionmentAccount, setSelectedApportionmentAccount] = useState('');
    const [apportionmentLoaded, setApportionmentLoaded] = useState(false);

    const corporateOnlyAccountNames = ['役員報酬', '役員賞与', '役員退職金'];

    const availableApportionmentAccounts = useMemo(() => {
        return accounts.filter(a =>
            inferAccountType(a) === ACCOUNT_TYPES.EXPENSE &&
            (String(a.code || a.id).startsWith('5') ||
                String(a.code || a.id).startsWith('6') ||
                String(a.code || a.id).startsWith('7')) &&
            !corporateOnlyAccountNames.some(name => a.name.includes(name))
        );
    }, [accounts]);

    useEffect(() => {
        if (isKojin && fileSystem && currentFiscalYear && !apportionmentLoaded) {
            setIsLoadingApportionment(true);
            fileSystem.getApportionment(currentFiscalYear).then(data => {
                if (data) {
                    setApportionmentSettings(data);
                    setSavedApportionmentSettings(data); // 保存済み状態を記録
                }
                setApportionmentLoaded(true);
            }).catch(err => {
                console.error('Failed to load apportionment:', err);
                setApportionmentLoaded(true);
            }).finally(() => {
                setIsLoadingApportionment(false);
            });
        }
    }, [isKojin, currentFiscalYear, fileSystem, apportionmentLoaded]);

    const saveApportionmentSettings = async () => {
        if (!fileSystem || !currentFiscalYear) return;
        setIsSavingApportionment(true);
        try {
            const dataToSave = {
                ...apportionmentSettings,
                updatedAt: new Date().toISOString()
            };
            const success = await fileSystem.saveApportionment(currentFiscalYear, dataToSave);
            if (success) {
                setApportionmentSettings(dataToSave);
                setSavedApportionmentSettings(dataToSave); // 保存済み状態を更新
                showAlert('家事按分設定を保存しました');
            } else {
                showAlert('保存に失敗しました', 'error');
            }
        } catch (err) {
            console.error('Failed to save apportionment:', err);
            showAlert('保存に失敗しました', 'error');
        } finally {
            setIsSavingApportionment(false);
        }
    };

    const updateApportionmentRatio = (accountId, field, value) => {
        const account = accounts.find(a => a.id === accountId);
        setApportionmentSettings(prev => ({
            ...prev,
            ratios: {
                ...prev.ratios,
                [accountId]: {
                    ...prev.ratios[accountId],
                    accountId,
                    accountName: account?.name || '',
                    [field]: value
                }
            }
        }));
    };

    const addApportionmentAccount = (accountId) => {
        const account = accounts.find(a => a.id === accountId);
        if (!account) return;
        setApportionmentSettings(prev => ({
            ...prev,
            ratios: {
                ...prev.ratios,
                [accountId]: {
                    accountId,
                    accountName: account.name,
                    businessRatio: '',
                    memo: ''
                }
            }
        }));
    };

    const removeApportionmentAccount = (accountId) => {
        setApportionmentSettings(prev => {
            const newRatios = { ...prev.ratios };
            delete newRatios[accountId];
            return { ...prev, ratios: newRatios };
        });
    };

    const handleManualSave = async () => {
        const success = await saveData();
        if (success) {
            showAlert('データを保存しました');
        } else {
            showAlert('保存に失敗しました', 'error');
        }
    };

    const exportToYayoiCSV = () => {
        const csvContent = fileSystem.journalsToCSV(journals, accounts);
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hanihohetoilo_${currentFiscalYear}_journals.csv`;
        a.click();
        URL.revokeObjectURL(url);
        showAlert('仕訳データをCSVエクスポートしました（弥生会計形式）');
    };

    const exportAccountsCSV = () => {
        const csvContent = fileSystem.accountsToCSV(accounts);
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hanihohetoilo_${currentFiscalYear}_accounts.csv`;
        a.click();
        URL.revokeObjectURL(url);
        showAlert('勘定科目をCSVエクスポートしました');
    };

    const readCsvWithEncoding = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const buffer = e.target.result;
                try {
                    const sjisDecoder = new TextDecoder('shift_jis');
                    const sjisText = sjisDecoder.decode(buffer);
                    if ((sjisText.match(/\uFFFD/g) || []).length < 3) {
                        resolve(sjisText);
                        return;
                    }
                } catch (e) { }
                try {
                    const utf8Decoder = new TextDecoder('utf-8');
                    const utf8Text = utf8Decoder.decode(buffer);
                    resolve(utf8Text);
                } catch (e) {
                    reject(new Error('ファイルのエンコーディングを判定できませんでした'));
                }
            };
            reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'));
            reader.readAsArrayBuffer(file);
        });
    };

    const importYayoiCSV = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const csvText = await readCsvWithEncoding(file);
            const firstLine = csvText.replace(/^\uFEFF/, '').split(/\r?\n/)[0].toLowerCase();
            const isAccountsCSV = (firstLine.includes('code') || firstLine.includes('"code"')) &&
                (firstLine.includes('statement') || firstLine.includes('"statement"'));
            const isJournalsCSV = firstLine.includes('識別フラグ') || firstLine.includes('借方科目');

            if (isAccountsCSV && !isJournalsCSV) {
                showAlert('インポートするファイルの種類が違います（勘定科目CSVが選択されています）', 'error');
                event.target.value = '';
                return;
            }

            const result = fileSystem.csvToJournals(csvText, accounts, { existingJournals: journals });
            const importedJournals = result.journals;
            const newAccounts = result.newAccounts;
            const skippedCount = result.skippedCount || 0;

            if (importedJournals.length > 0) {
                let message = `${importedJournals.length}件の仕訳をインポートしますか？`;
                if (skippedCount > 0) {
                    message += `\n（${skippedCount}件は重複のためスキップ）`;
                }
                if (newAccounts.length > 0) {
                    message += `\n\n※ ${newAccounts.length}件の新しい勘定科目が自動追加されます。\n（${newAccounts.map(a => a.name).join('、')}）`;
                }
                if (confirm(message)) {
                    const updatedAccounts = newAccounts.length > 0
                        ? [...accounts, ...newAccounts]
                        : accounts;
                    const updatedJournals = [...journals, ...importedJournals];

                    setAccounts(updatedAccounts);
                    setJournals(updatedJournals);

                    let successMessage = `${importedJournals.length}件の仕訳をインポートしました`;
                    if (skippedCount > 0) {
                        successMessage += `（${skippedCount}件重複スキップ）`;
                    }
                    showAlert(successMessage);
                }
            } else if (skippedCount > 0) {
                showAlert(`全て重複のためスキップされました（${skippedCount}件）`, 'error');
            } else {
                showAlert('インポートできる仕訳がありませんでした', 'error');
            }
        } catch (err) {
            showAlert('ファイルの読み込みに失敗しました: ' + err.message, 'error');
        }
        event.target.value = '';
    };

    const importAccountsCSV = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const csvText = await readCsvWithEncoding(file);
            const firstLine = csvText.replace(/^\uFEFF/, '').split(/\r?\n/)[0];
            const hasJournalHeader = firstLine.includes('識別フラグ') || firstLine.includes('借方科目') || firstLine.includes('取引日付');
            const firstField = firstLine.split(',')[0].replace(/"/g, '').trim();
            const isYayoiData = /^(2000|2100|2110|2101|2111)$/.test(firstField);

            if (hasJournalHeader || isYayoiData) {
                showAlert('インポートするファイルの種類が違います（仕訳CSVが選択されています）', 'error');
                event.target.value = '';
                return;
            }

            const importedAccounts = fileSystem.csvToAccounts(csvText);

            if (importedAccounts && importedAccounts.error === 'journals') {
                showAlert('インポートするファイルの種類が違います（仕訳CSVが選択されています）', 'error');
                event.target.value = '';
                return;
            }

            if (importedAccounts && Array.isArray(importedAccounts) && importedAccounts.length > 0) {
                if (confirm(`${importedAccounts.length}件の勘定科目をインポートしますか？現在の勘定科目は上書きされます。`)) {
                    // BUG-1修正: 科目名で照合し、既存IDを保持
                    const mergedAccounts = importedAccounts.map(imported => {
                        // 既存科目と名前で照合
                        const existing = accounts.find(a => a.name === imported.name);
                        if (existing) {
                            // 既存IDを保持し、属性だけ更新
                            return { ...imported, id: existing.id };
                        } else {
                            // 新規科目: UUIDがなければ生成
                            if (!imported.id || imported.id === imported.code) {
                                return { ...imported, id: `acc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` };
                            }
                            return imported;
                        }
                    });

                    // 既存にあってCSVにない科目は残す（仕訳が参照中の可能性）
                    const importedNames = new Set(mergedAccounts.map(a => a.name));
                    const retainedAccounts = accounts.filter(a => !importedNames.has(a.name));

                    const finalAccounts = [...mergedAccounts, ...retainedAccounts];
                    setAccounts(finalAccounts);

                    // BUG-2修正: 家事按分設定のaccountIdを再マッピング
                    if (apportionmentSettings.ratios && Object.keys(apportionmentSettings.ratios).length > 0) {
                        const remappedRatios = {};
                        Object.entries(apportionmentSettings.ratios).forEach(([oldAccountId, setting]) => {
                            // 旧accountIdから科目名を取得
                            const oldAccount = accounts.find(a => a.id === oldAccountId);
                            if (oldAccount) {
                                // 新しいaccountsから同名の科目を探す
                                const newAccount = finalAccounts.find(a => a.name === oldAccount.name);
                                if (newAccount) {
                                    // 新しいaccountIdでマッピング
                                    remappedRatios[newAccount.id] = {
                                        ...setting,
                                        accountId: newAccount.id,
                                        accountName: newAccount.name
                                    };
                                } else {
                                    // 科目が見つからない場合は元のIDで保持
                                    remappedRatios[oldAccountId] = setting;
                                }
                            } else {
                                // 元のIDでも見つからない場合はそのまま
                                remappedRatios[oldAccountId] = setting;
                            }
                        });
                        setApportionmentSettings(prev => ({
                            ...prev,
                            ratios: remappedRatios
                        }));
                        console.log('家事按分設定を再マッピングしました');
                    }

                    showAlert(`${importedAccounts.length}件の勘定科目をインポートしました（既存との統合: ${mergedAccounts.filter((a, i) => accounts.find(ex => ex.name === a.name)).length}件）`);
                }
            } else {
                showAlert('インポートできる勘定科目がありませんでした', 'error');
            }
        } catch (err) {
            showAlert('ファイルの読み込みに失敗しました: ' + err.message, 'error');
        }
        event.target.value = '';
    };

    const clearJournals = () => {
        if (journals.length === 0) {
            showAlert('削除する仕訳データがありません', 'error');
            return;
        }
        if (confirm(`現在の年度（${currentFiscalYear}年度）の仕訳データ（${journals.length}件）をすべて削除しますか？この操作は取り消せません。`)) {
            if (confirm('本当に削除してよろしいですか？')) {
                const deletedAt = new Date().toISOString();
                const newDeletedRecords = journals.map(j => ({
                    ...j,
                    deletedAt
                }));
                setDeletedJournals([...deletedJournals, ...newDeletedRecords]);
                setJournals([]);
                showAlert('仕訳データを削除しました（削除済リストに記録されました）');
            }
        }
    };

    const resetAccounts = () => {
        if (confirm('勘定科目をデフォルトに戻しますか？この操作は取り消せません。')) {
            if (confirm('本当にデフォルトに戻してよろしいですか？')) {
                // BUG-1修正: 名前が一致する科目は既存IDを保持する
                const resetAccounts = DEFAULT_ACCOUNTS.map(defaultAcc => {
                    const existing = accounts.find(a => a.name === defaultAcc.name);
                    if (existing) {
                        // 既存IDを保持
                        return { ...defaultAcc, id: existing.id };
                    }
                    return defaultAcc;
                });

                setAccounts(resetAccounts);

                // BUG-2修正: 家事按分設定のaccountIdを再マッピング
                if (apportionmentSettings.ratios && Object.keys(apportionmentSettings.ratios).length > 0) {
                    const remappedRatios = {};
                    Object.entries(apportionmentSettings.ratios).forEach(([oldAccountId, setting]) => {
                        const oldAccount = accounts.find(a => a.id === oldAccountId);
                        if (oldAccount) {
                            const newAccount = resetAccounts.find(a => a.name === oldAccount.name);
                            if (newAccount) {
                                remappedRatios[newAccount.id] = {
                                    ...setting,
                                    accountId: newAccount.id,
                                    accountName: newAccount.name
                                };
                            } else {
                                remappedRatios[oldAccountId] = setting;
                            }
                        } else {
                            remappedRatios[oldAccountId] = setting;
                        }
                    });
                    setApportionmentSettings(prev => ({
                        ...prev,
                        ratios: remappedRatios
                    }));
                }

                showAlert('勘定科目をデフォルトに戻しました');
            }
        }
    };

    const addBankAccount = () => {
        setDetailSettings(prev => ({
            ...prev,
            bankAccounts: [...prev.bankAccounts, { name: '', branch: '', type: '普通', number: '', accountId: '' }]
        }));
    };

    const addPartner = () => {
        setDetailSettings(prev => ({
            ...prev,
            partners: [...prev.partners, { name: '', address: '', type: 'customer' }]
        }));
    };

    const addOfficer = () => {
        setDetailSettings(prev => ({
            ...prev,
            officers: [...prev.officers, { name: '', title: '', salary: 0 }]
        }));
    };

    const addRental = () => {
        setDetailSettings(prev => ({
            ...prev,
            rentals: [...prev.rentals, { property: '', address: '', landlord: '', monthlyRent: 0 }]
        }));
    };

    const updateDetailItem = (category, index, field, value) => {
        setDetailSettings(prev => ({
            ...prev,
            [category]: prev[category].map((item, i) => i === index ? { ...item, [field]: value } : item)
        }));
    };

    const removeDetailItem = (category, index) => {
        setDetailSettings(prev => ({
            ...prev,
            [category]: prev[category].filter((_, i) => i !== index)
        }));
    };

    const tabStyle = (tabName) => ({
        padding: '10px 20px',
        border: 'none',
        borderBottom: activeTab === tabName ? '3px solid #3498db' : '3px solid transparent',
        background: activeTab === tabName ? '#fff' : '#f8f9fa',
        cursor: 'pointer',
        fontWeight: activeTab === tabName ? 'bold' : 'normal'
    });

    return (
        <div>
            <div className="page-header">
                <h2>データ管理</h2>
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button style={tabStyle('year')} onClick={() => handleTabChange('year')}>年度・保存</button>
                <button style={tabStyle('org')} onClick={() => handleTabChange('org')}>組織情報</button>
                <button style={tabStyle('vendors')} onClick={() => handleTabChange('vendors')}>取引先</button>
                <button style={tabStyle('csv')} onClick={() => handleTabChange('csv')}>CSV入出力</button>
                <button style={tabStyle('detail')} onClick={() => handleTabChange('detail')}>内訳書設定</button>
                {showAllocation && <button style={tabStyle('allocation')} onClick={() => handleTabChange('allocation')}>配賦設定</button>}
                {isKojin && <button style={tabStyle('apportionment')} onClick={() => handleTabChange('apportionment')}>家事按分設定</button>}
                <button style={tabStyle('credit-card')} onClick={() => handleTabChange('credit-card')}>クレジットカード</button>
                {isBookOwner && <button style={tabStyle('members')} onClick={() => handleTabChange('members')}>メンバー</button>}
                <button style={tabStyle('delete')} onClick={() => handleTabChange('delete')}>削除</button>
            </div>

            {activeTab === 'year' && (
                <>
                    <div className="card">
                        <h3 className="card-title">年度管理</h3>
                        {(() => {
                            const currentFy = fiscalYears.find(fy => fy.year === currentFiscalYear);
                            const endMonth = currentFy?.endMonth || 3;
                            const startMonth = endMonth === 12 ? 1 : endMonth + 1;
                            const startYear = endMonth === 12 ? currentFiscalYear : currentFiscalYear;
                            const endYear = endMonth === 12 ? currentFiscalYear : currentFiscalYear + 1;
                            const period = isKojin
                                ? `${currentFiscalYear}年（${currentFiscalYear}/1〜${currentFiscalYear}/12）`
                                : `${currentFiscalYear}年度（${startYear}/${startMonth}〜${endYear}/${endMonth}）`;

                            return (
                                <p style={{ marginBottom: '16px', color: '#666' }}>
                                    現在選択中: <strong>{period}</strong>
                                </p>
                            );
                        })()}

                        <div className="form-row">
                            <div className="form-group">
                                <label>新規年度作成</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={newYear}
                                        onChange={(e) => setNewYear(Number(e.target.value))}
                                        min="2020"
                                        max="2100"
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => createNewFiscalYear(newYear)}
                                    >
                                        作成
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <h4>登録済み年度</h4>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                {[...fiscalYears].sort((a, b) => b.year - a.year).map(fy => (
                                    <span
                                        key={fy.year}
                                        className={`badge ${fy.year === currentFiscalYear ? 'badge-asset' : ''}`}
                                        style={{ padding: '8px 16px', fontSize: '14px' }}
                                    >
                                        {fy.year}{isKojin ? '年' : `年度（${fy.endMonth}月決算）`}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <h3 className="card-title">現在のデータ状況</h3>
                        <table>
                            <tbody>
                                <tr>
                                    <td>勘定科目数</td>
                                    <td className="text-right">{accounts.length}件</td>
                                </tr>
                                <tr>
                                    <td>仕訳件数</td>
                                    <td className="text-right">{journals.length}件</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {activeTab === 'org' && (
                <div className="card">
                    <h3 className="card-title">組織情報の変更</h3>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
                        組織種別や名称を変更できます。変更はサーバーに保存されます。
                    </p>

                    <div className="form-row">
                        <div className="form-group">
                            <label>組織種別</label>
                            <select
                                className="form-control"
                                value={editOrgType}
                                onChange={(e) => setEditOrgType(e.target.value)}
                            >
                                <option value={ORG_TYPES.KOJIN}>{ORG_TYPE_LABELS[ORG_TYPES.KOJIN]}</option>
                                <option value={ORG_TYPES.IPPAN_SHADAN}>{ORG_TYPE_LABELS[ORG_TYPES.IPPAN_SHADAN]}</option>
                            </select>
                        </div>
                    </div>

                    {editOrgType === ORG_TYPES.IPPAN_SHADAN && (
                        <div className="form-row">
                            <div className="form-group">
                                <label>法人の種類</label>
                                <select
                                    className="form-control"
                                    value={editOrgSubType}
                                    onChange={(e) => setEditOrgSubType(e.target.value)}
                                >
                                    <option value="nonprofit">非営利型（収益事業のみ課税）</option>
                                    <option value="forprofit">普通型・営利型（全所得課税）</option>
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="form-row">
                        <div className="form-group">
                            <label>{isIndividual(editOrgType) ? '屋号・氏名' : '法人名'}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={editOrgName}
                                onChange={(e) => setEditOrgName(e.target.value)}
                                placeholder={isIndividual(editOrgType) ? '例: 山田太郎 / ○○事務所' : '例: 株式会社○○'}
                            />
                        </div>
                    </div>

                    {!isIndividual(editOrgType) && (
                        <div className="form-row">
                            <div className="form-group">
                                <label>設立年度（任意）</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editFoundingYear}
                                    onChange={(e) => setEditFoundingYear(e.target.value)}
                                    placeholder="例: 2020"
                                    min="1900"
                                    max="2100"
                                    style={{ width: '200px' }}
                                />
                                <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                    設定すると「第◯期」の期数が表示されます
                                </p>
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: '20px' }}>
                        <button
                            className="btn btn-primary"
                            disabled={isSavingOrg}
                            onClick={async () => {
                                if (!user?.email) {
                                    showAlert('ログインが必要です', 'error');
                                    return;
                                }
                                setIsSavingOrg(true);
                                try {
                                    const newOrgInfo = {
                                        ...orgInfo,
                                        type: editOrgType,
                                        name: editOrgName,
                                        ...(editOrgType === ORG_TYPES.IPPAN_SHADAN ? { subType: editOrgSubType } : {}),
                                        ...(editFoundingYear ? { foundingYear: Number(editFoundingYear) } : {})
                                    };
                                    const success = await saveOrgInfo(newOrgInfo, activeBookEmail || user.email);
                                    if (success) {
                                        setOrgInfo(newOrgInfo);
                                        // localStorageも更新してサイドバーに即時反映
                                        localStorage.setItem('toiro_org_info', JSON.stringify(newOrgInfo));
                                        showAlert('組織情報を保存しました');
                                    } else {
                                        showAlert('保存に失敗しました', 'error');
                                    }
                                } catch (error) {
                                    console.error('Failed to save org info:', error);
                                    showAlert('保存に失敗しました', 'error');
                                } finally {
                                    setIsSavingOrg(false);
                                }
                            }}
                        >
                            {isSavingOrg ? '保存中...' : '保存'}
                        </button>
                    </div>

                    <div style={{ marginTop: '24px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '12px' }}>現在の設定</h4>
                        <table style={{ fontSize: '13px' }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '4px 16px 4px 0', color: '#666' }}>組織種別:</td>
                                    <td>{ORG_TYPE_LABELS[orgInfo?.type] || orgInfo?.type || '(未設定)'}</td>
                                </tr>
                                {orgInfo?.type === ORG_TYPES.IPPAN_SHADAN && (
                                    <tr>
                                        <td style={{ padding: '4px 16px 4px 0', color: '#666' }}>法人の種類:</td>
                                        <td>{orgInfo?.subType === 'forprofit' ? '普通型（営利型）' : '非営利型'}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td style={{ padding: '4px 16px 4px 0', color: '#666' }}>名称:</td>
                                    <td>{orgInfo?.name || '(未設定)'}</td>
                                </tr>
                                {orgInfo?.foundingYear && (
                                    <tr>
                                        <td style={{ padding: '4px 16px 4px 0', color: '#666' }}>設立年度:</td>
                                        <td>{orgInfo.foundingYear}年（第{currentFiscalYear - orgInfo.foundingYear + 1}期）</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'vendors' && (
                <div className="card">
                    <h3 className="card-title">登録済み取引先</h3>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                        仕訳入力時に課税取引（8%以上）で入力した取引先が一覧表示されます。
                    </p>
                    {vendors.length > 0 ? (
                        <table className="data-table" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '140px' }}>登録番号</th>
                                    <th>取引先名</th>
                                    <th style={{ width: '150px' }}>備考</th>
                                    <th style={{ width: '60px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendors.map(v => (
                                    <tr key={v.invoiceNumber}>
                                        <td style={{ fontFamily: 'monospace' }}>T{v.invoiceNumber}</td>
                                        <td>{v.vendorName}</td>
                                        <td style={{ color: '#666' }}>{v.vendorMemo || '-'}</td>
                                        <td>
                                            <button
                                                className="btn btn-danger"
                                                style={{ padding: '2px 8px', fontSize: '11px' }}
                                                onClick={() => {
                                                    if (confirm(`「${v.vendorName}」を削除しますか？`)) {
                                                        deleteVendor(v.invoiceNumber);
                                                    }
                                                }}
                                            >削除</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p style={{ color: '#999', fontStyle: 'italic' }}>登録済みの取引先はありません</p>
                    )}
                </div>
            )}

            {activeTab === 'csv' && (
                <>
                    <div className="card">
                        <h3 className="card-title">CSVエクスポート（弥生会計形式）</h3>
                        <div className="btn-group" style={{ flexWrap: 'wrap' }}>
                            <button className="btn btn-success" onClick={exportToYayoiCSV}>
                                仕訳データCSV
                            </button>
                            <button className="btn btn-success" onClick={exportAccountsCSV}>
                                勘定科目CSV
                            </button>
                        </div>
                        <p style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
                            ※ 弥生会計互換のCSV形式でエクスポートします。
                        </p>
                    </div>

                    <div className="card">
                        <h3 className="card-title">CSVインポート</h3>
                        <div className="btn-group" style={{ flexWrap: 'wrap' }}>
                            <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                                仕訳データCSVインポート
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={importYayoiCSV}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                                勘定科目CSVインポート
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={importAccountsCSV}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>
                        <p style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
                            ※ 弥生会計形式のCSVをインポートできます。仕訳データは既存データに追加されます。
                        </p>
                    </div>
                </>
            )}

            {activeTab === 'detail' && (
                <>
                    <div className="card">
                        <h3 className="card-title">預貯金等の内訳書</h3>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                            法人税申告書 勘定科目内訳明細書（預貯金等）に記載する銀行口座情報
                        </p>
                        <table style={{ width: '100%', marginBottom: '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>金融機関名</th>
                                    <th style={{ width: '15%' }}>支店名</th>
                                    <th style={{ width: '10%' }}>種類</th>
                                    <th style={{ width: '15%' }}>口座番号</th>
                                    <th style={{ width: '25%' }}>勘定科目</th>
                                    <th style={{ width: '15%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailSettings.bankAccounts.map((bank, i) => (
                                    <tr key={i}>
                                        <td><input className="form-control" value={bank.name} onChange={(e) => updateDetailItem('bankAccounts', i, 'name', e.target.value)} placeholder="○○銀行" /></td>
                                        <td><input className="form-control" value={bank.branch} onChange={(e) => updateDetailItem('bankAccounts', i, 'branch', e.target.value)} placeholder="△△支店" /></td>
                                        <td>
                                            <select className="form-control" value={bank.type} onChange={(e) => updateDetailItem('bankAccounts', i, 'type', e.target.value)}>
                                                <option value="普通">普通</option>
                                                <option value="当座">当座</option>
                                                <option value="定期">定期</option>
                                            </select>
                                        </td>
                                        <td><input className="form-control" value={bank.number} onChange={(e) => updateDetailItem('bankAccounts', i, 'number', e.target.value)} placeholder="1234567" /></td>
                                        <td>
                                            <select className="form-control" value={bank.accountId} onChange={(e) => updateDetailItem('bankAccounts', i, 'accountId', e.target.value)}>
                                                <option value="">選択...</option>
                                                {accounts.filter(a => a.category === '資産' && (a.name.includes('預金') || a.name.includes('現金'))).map(a => (
                                                    <option key={a.id} value={a.id}>{a.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td><button className="btn btn-danger btn-sm" onClick={() => removeDetailItem('bankAccounts', i)}>削除</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button className="btn btn-primary btn-sm" onClick={addBankAccount}>+ 口座を追加</button>
                    </div>

                    <div className="card">
                        <h3 className="card-title">役員報酬の内訳書</h3>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                            役員報酬手当等及び人件費の内訳書用
                        </p>
                        <table style={{ width: '100%', marginBottom: '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '30%' }}>氏名</th>
                                    <th style={{ width: '25%' }}>役職</th>
                                    <th style={{ width: '25%' }}>月額報酬</th>
                                    <th style={{ width: '20%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailSettings.officers.map((officer, i) => (
                                    <tr key={i}>
                                        <td><input className="form-control" value={officer.name} onChange={(e) => updateDetailItem('officers', i, 'name', e.target.value)} placeholder="氏名" /></td>
                                        <td><input className="form-control" value={officer.title} onChange={(e) => updateDetailItem('officers', i, 'title', e.target.value)} placeholder="代表理事" /></td>
                                        <td><input className="form-control" type="number" value={officer.salary} onChange={(e) => updateDetailItem('officers', i, 'salary', Number(e.target.value))} /></td>
                                        <td><button className="btn btn-danger btn-sm" onClick={() => removeDetailItem('officers', i)}>削除</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button className="btn btn-primary btn-sm" onClick={addOfficer}>+ 役員を追加</button>
                    </div>

                    <div className="card">
                        <h3 className="card-title">地代家賃の内訳書</h3>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                            地代家賃等の内訳書用
                        </p>
                        <table style={{ width: '100%', marginBottom: '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>物件名</th>
                                    <th style={{ width: '25%' }}>所在地</th>
                                    <th style={{ width: '20%' }}>貸主</th>
                                    <th style={{ width: '15%' }}>月額</th>
                                    <th style={{ width: '20%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailSettings.rentals.map((rental, i) => (
                                    <tr key={i}>
                                        <td><input className="form-control" value={rental.property} onChange={(e) => updateDetailItem('rentals', i, 'property', e.target.value)} placeholder="物件名" /></td>
                                        <td><input className="form-control" value={rental.address} onChange={(e) => updateDetailItem('rentals', i, 'address', e.target.value)} placeholder="所在地" /></td>
                                        <td><input className="form-control" value={rental.landlord} onChange={(e) => updateDetailItem('rentals', i, 'landlord', e.target.value)} placeholder="貸主名" /></td>
                                        <td><input className="form-control" type="number" value={rental.monthlyRent} onChange={(e) => updateDetailItem('rentals', i, 'monthlyRent', Number(e.target.value))} /></td>
                                        <td><button className="btn btn-danger btn-sm" onClick={() => removeDetailItem('rentals', i)}>削除</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button className="btn btn-primary btn-sm" onClick={addRental}>+ 物件を追加</button>
                    </div>
                </>
            )}

            {showAllocation && activeTab === 'allocation' && (
                <>
                    <div className="card">
                        <h3 className="card-title">共通経費の配賦設定</h3>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                            「共通」区分で入力した経費を、収益事業と非収益事業に按分するための設定です。
                        </p>

                        <div className="form-group">
                            <label>配賦基準</label>
                            <select
                                className="form-control"
                                value={allocationSettings.method}
                                onChange={(e) => setAllocationSettings(prev => ({ ...prev, method: e.target.value }))}
                                style={{ maxWidth: '300px' }}
                            >
                                <option value="revenue">収益比率（各事業の収益額の比率）</option>
                                <option value="labor">人件費比率（各事業の人件費の比率）</option>
                                <option value="area">面積比率（使用面積を手入力）</option>
                                <option value="direct">直接費比率（各事業の直接費の比率）</option>
                                <option value="custom">カスタム（手入力）</option>
                            </select>
                        </div>

                        {allocationSettings.method === 'custom' || allocationSettings.method === 'area' ? (
                            <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                                <h4 style={{ marginBottom: '16px', fontSize: '15px' }}>配賦割合の設定</h4>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label>収益事業への配賦</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="number"
                                                className="form-control"
                                                style={{ width: '100px' }}
                                                min="0"
                                                max="100"
                                                value={allocationSettings.revenueRatio}
                                                onChange={(e) => {
                                                    const val = Math.min(100, Math.max(0, Number(e.target.value)));
                                                    setAllocationSettings(prev => ({
                                                        ...prev,
                                                        revenueRatio: val,
                                                        nonRevenueRatio: 100 - val
                                                    }));
                                                }}
                                            />
                                            <span>%</span>
                                        </div>
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label>非収益事業への配賦</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="number"
                                                className="form-control"
                                                style={{ width: '100px', background: '#e9ecef' }}
                                                value={allocationSettings.nonRevenueRatio}
                                                disabled
                                            />
                                            <span>%</span>
                                        </div>
                                    </div>
                                </div>
                                <p style={{ marginTop: '12px', fontSize: '13px', color: '#888' }}>
                                    ※ 合計が100%になるよう自動調整されます
                                </p>
                            </div>
                        ) : (
                            <div style={{ marginTop: '20px', padding: '20px', background: '#e8f5e9', borderRadius: '8px' }}>
                                <p style={{ fontSize: '14px', color: '#2e7d32' }}>
                                    「{allocationSettings.method === 'revenue' ? '収益比率' :
                                        allocationSettings.method === 'labor' ? '人件費比率' : '直接費比率'}」
                                    を選択中です。<br />
                                    仕訳データから自動的に配賦割合が計算されます。
                                </p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {isKojin && activeTab === 'apportionment' && (
                <>
                    <div className="card">
                        <h3 className="card-title">家事按分設定</h3>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                            事業とプライベートで共用している経費の事業割合を設定します。<br />
                            設定した割合は決算整理の「家事按分」で自動反映されます。
                        </p>

                        {isLoadingApportionment ? (
                            <p style={{ textAlign: 'center', padding: '40px' }}>読み込み中...</p>
                        ) : (
                            <>
                                <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div style={{ width: '300px' }}>
                                        <AccountSelect
                                            accounts={availableApportionmentAccounts.filter(a => !apportionmentSettings.ratios[a.id])}
                                            value={selectedApportionmentAccount}
                                            onChange={(id) => setSelectedApportionmentAccount(id)}
                                            placeholder="科目を検索..."
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            if (selectedApportionmentAccount) {
                                                addApportionmentAccount(selectedApportionmentAccount);
                                                setSelectedApportionmentAccount('');
                                            }
                                        }}
                                        disabled={!selectedApportionmentAccount}
                                    >
                                        科目を追加
                                    </button>
                                </div>

                                {Object.keys(apportionmentSettings.ratios).length === 0 ? (
                                    <div style={{ padding: '40px', textAlign: 'center', color: '#888', background: '#f8f9fa', borderRadius: '8px' }}>
                                        <p>家事按分が必要な科目を上のドロップダウンから追加してください。</p>
                                    </div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '25%' }}>勘定科目</th>
                                                <th style={{ width: '15%', textAlign: 'center' }}>事業割合</th>
                                                <th style={{ width: '50%' }}>按分根拠メモ</th>
                                                <th style={{ width: '10%' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(apportionmentSettings.ratios).map(([accountId, ratio]) => {
                                                const account = accounts.find(a => a.id === accountId);
                                                return (
                                                    <tr key={accountId}>
                                                        <td>{ratio.accountName || account?.name || accountId}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                                <input
                                                                    type="text"
                                                                    inputMode="numeric"
                                                                    className="form-control"
                                                                    value={ratio.businessRatio ?? ''}
                                                                    onChange={(e) => {
                                                                        // 全角→半角変換
                                                                        const normalized = e.target.value.replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
                                                                        if (normalized === '') {
                                                                            updateApportionmentRatio(accountId, 'businessRatio', '');
                                                                        } else {
                                                                            const num = parseInt(normalized) || 0;
                                                                            // 100を超えたら100に制限
                                                                            updateApportionmentRatio(accountId, 'businessRatio', Math.min(100, Math.max(0, num)));
                                                                        }
                                                                    }}
                                                                    placeholder="0-100"
                                                                    style={{ width: '100px', textAlign: 'right', fontSize: '16px' }}
                                                                />
                                                                <span>%</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={ratio.memo || ''}
                                                                onChange={(e) => updateApportionmentRatio(accountId, 'memo', e.target.value)}
                                                                placeholder="例: 作業部屋 6畳 / 全体 10畳"
                                                                style={{ width: '100%' }}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => removeApportionmentAccount(accountId)}
                                                            >
                                                                削除
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}

                                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <button
                                        className="btn btn-primary"
                                        onClick={saveApportionmentSettings}
                                        disabled={isSavingApportionment}
                                    >
                                        {isSavingApportionment ? '保存中...' : '設定を保存'}
                                    </button>
                                    {apportionmentSettings.updatedAt && (
                                        <span style={{ color: '#666', fontSize: '13px' }}>
                                            最終更新: {new Date(apportionmentSettings.updatedAt).toLocaleString('ja-JP')}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="card" style={{ marginTop: '20px', background: '#f8f9fa' }}>
                        <h4 style={{ marginBottom: '12px' }}>使い方</h4>
                        <ol style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#555' }}>
                            <li>「科目を追加」ボタンで按分が必要な経費科目を追加します</li>
                            <li>「事業割合」に事業で使用している割合を入力します（100%未満＝一部家事使用）</li>
                            <li>「按分根拠メモ」に按分の根拠を記録しておくと、税務調査時に説明しやすくなります</li>
                            <li>「設定を保存」ボタンで設定を保存します</li>
                            <li>決算整理の「家事按分」タブで、この設定に基づいて仕訳を作成できます</li>
                        </ol>
                    </div>
                </>
            )}

            {activeTab === 'credit-card' && (
                <div className="card">
                    <h3 className="card-title">クレジットカード設定</h3>

                    {/* 登録済みカード一覧 */}
                    {creditCards.length > 0 && (
                        <div style={{ marginBottom: '24px' }}>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>カード名</th>
                                        <th>締め日</th>
                                        <th>引落し日</th>
                                        <th>引落し口座</th>
                                        <th style={{ width: '100px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creditCards.map(card => (
                                        <tr key={card.id}>
                                            <td>{card.name}</td>
                                            <td>{card.closingDay === 31 ? '末日' : `${card.closingDay}日`}</td>
                                            <td>{card.paymentMonthOffset === 1 ? '翌月' : '翌々月'}{card.paymentDay}日</td>
                                            <td>{accounts.find(a => a.id === card.paymentAccountId)?.name || '未設定'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <button className="btn btn-secondary btn-small" onClick={() => handleEditCard(card)}>編集</button>
                                                    <button className="btn btn-danger btn-small" onClick={() => handleDeleteCard(card.id)}>削除</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {creditCards.length === 0 && (
                        <p style={{ color: '#888', marginBottom: '16px' }}>登録されたクレジットカードはありません。</p>
                    )}

                    {/* カード登録/編集フォーム */}
                    <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                        <h4 style={{ marginBottom: '12px' }}>{editingCard ? 'カードを編集' : '新しいカードを登録'}</h4>
                        <div className="form-row">
                            <div className="form-group">
                                <label>カード名</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={cardForm.name}
                                    onChange={e => setCardForm({ ...cardForm, name: e.target.value })}
                                    placeholder="例: JCBカード"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>締め日</label>
                                <select
                                    className="form-control"
                                    value={cardForm.closingDay}
                                    onChange={e => setCardForm({ ...cardForm, closingDay: Number(e.target.value) })}
                                >
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                                        <option key={d} value={d}>{d === 31 ? '末日' : `${d}日`}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>引落しタイミング</label>
                                <select
                                    className="form-control"
                                    value={cardForm.paymentMonthOffset}
                                    onChange={e => setCardForm({ ...cardForm, paymentMonthOffset: Number(e.target.value) })}
                                >
                                    <option value={1}>翌月</option>
                                    <option value={2}>翌々月</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>引落し日</label>
                                <select
                                    className="form-control"
                                    value={cardForm.paymentDay}
                                    onChange={e => setCardForm({ ...cardForm, paymentDay: Number(e.target.value) })}
                                >
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                                        <option key={d} value={d}>{d === 31 ? '末日' : `${d}日`}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>引落し口座</label>
                                <select
                                    className="form-control"
                                    value={cardForm.paymentAccountId}
                                    onChange={e => setCardForm({ ...cardForm, paymentAccountId: e.target.value })}
                                >
                                    <option value="">選択してください</option>
                                    {assetAccounts.map(a => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>未払金科目</label>
                                <select
                                    className="form-control"
                                    value={cardForm.unpaidAccountId}
                                    onChange={e => setCardForm({ ...cardForm, unpaidAccountId: e.target.value })}
                                >
                                    <option value="">選択してください</option>
                                    {liabilityAccounts.map(a => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>開始残高</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formatAmountInput(cardForm.openingBalance)}
                                    onChange={e => setCardForm({ ...cardForm, openingBalance: parseAmount(e.target.value) })}
                                    style={{ textAlign: 'right' }}
                                />
                                <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                    アプリ利用開始前にすでに発生していた未精算の請求額。ダッシュボードの残高に加算されます。
                                </p>
                            </div>
                            <div className="form-group">
                                <label>基準日</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={cardForm.openingBalanceDate}
                                    onChange={e => setCardForm({ ...cardForm, openingBalanceDate: clampDate(e.target.value) })}
                                />
                                <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                    開始残高の基準となる日付（記録用。計算には影響しません）。
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveCard}
                                disabled={isSavingCards}
                            >
                                {isSavingCards ? '保存中...' : editingCard ? '更新' : '登録'}
                            </button>
                            {editingCard && (
                                <button className="btn btn-secondary" onClick={resetCardForm}>
                                    キャンセル
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isBookOwner && activeTab === 'members' && (
                <div className="card">
                    <h3 className="card-title">メンバー管理</h3>
                    <p style={{ color: '#666', marginBottom: '16px' }}>
                        メールアドレスを入力して「招待」を押すと、招待メールが作成されます。<br />
                        相手がメール内のリンクを踏んでGoogleログインすると、帳簿にアクセスできるようになります。
                    </p>

                    {/* 招待フォーム */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                        <input
                            type="email"
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleInviteMember()}
                            placeholder="メールアドレスを入力"
                            style={{
                                flex: 1, padding: '10px 12px', border: '1px solid #ddd',
                                borderRadius: '6px', fontSize: '14px'
                            }}
                        />
                        <button className="btn btn-primary" onClick={handleInviteMember} disabled={isInviting}>
                            {isInviting ? '送信中...' : '招待メール送信'}
                        </button>
                    </div>

                    {/* 承認待ちの招待 */}
                    {pendingInvites.length > 0 && (
                        <>
                            <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>承認待ち</h4>
                            <table className="data-table" style={{ width: '100%', marginBottom: '20px' }}>
                                <thead>
                                    <tr>
                                        <th>送信先</th>
                                        <th>招待日</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingInvites.map(inv => {
                                        const expired = isInviteExpired(inv.createdAt);
                                        return (
                                            <tr key={inv.token}>
                                                <td>{inv.inviteeEmail}</td>
                                                <td style={{ color: expired ? '#e74c3c' : undefined }}>
                                                    {expired ? '期限切れ' : new Date(inv.createdAt).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </td>
                                                <td>
                                                    {expired ? (
                                                        <button
                                                            className="btn btn-sm"
                                                            style={{ color: '#2980b9', fontSize: '13px' }}
                                                            onClick={() => handleReinvite(inv)}
                                                            disabled={isInviting}
                                                        >
                                                            再招待
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-sm"
                                                            style={{ color: '#e74c3c', fontSize: '13px' }}
                                                            onClick={() => handleCancelInvite(inv.token)}
                                                        >
                                                            取消
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* 承認済みメンバー */}
                    <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>メンバー</h4>
                    {members.length === 0 ? (
                        <p style={{ color: '#999' }}>承認済みのメンバーはいません</p>
                    ) : (
                        <table className="data-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>メールアドレス</th>
                                    <th>参加日</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.map(m => (
                                    <tr key={m.email}>
                                        <td>{m.email}</td>
                                        <td>{m.invitedAt ? new Date(m.invitedAt).toLocaleDateString('ja-JP') : '-'}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm"
                                                style={{ color: '#e74c3c', fontSize: '13px' }}
                                                onClick={() => handleRemoveMember(m.email)}
                                            >
                                                削除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {activeTab === 'delete' && (
                <div className="card" style={{ borderColor: '#e74c3c' }}>
                    <h3 className="card-title" style={{ color: '#e74c3c' }}>データの削除</h3>
                    <p style={{ marginBottom: '16px', color: '#666' }}>
                        現在の年度（{currentFiscalYear}年度）のデータを削除します。この操作は取り消せません。
                    </p>
                    <div className="btn-group">
                        <button className="btn btn-danger" onClick={clearJournals}>
                            仕訳データを削除（{journals.length}件）
                        </button>
                        <button className="btn btn-warning" onClick={resetAccounts}>
                            勘定科目をデフォルトに戻す
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataManagement;
